import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { formatMoney } from "@/lib/format";
import { StorefrontLayout } from "@/components/StorefrontLayout";
import { Button } from "@/components/Button";
import { MinusIcon, PlusIcon, TrashIcon } from "@/components/icons";

export function Cart() {
  const { items, updateQty, removeItem, totalCents, requiresDeposit } = useCart();
  const navigate = useNavigate();
  const currency = items[0]?.currency ?? "KES";

  return (
    <StorefrontLayout showNav={items.length === 0}>
      <div className="px-5 pb-32 pt-6">
        <h1 className="font-display text-2xl font-medium text-ink">Your Bag</h1>

        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <p className="text-sm text-stone-500">Your bag is empty.</p>
            <Link to="/" className="text-sm font-semibold text-ink underline">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-5">
            {items.map((item) => (
              <div key={item.productId} className="flex gap-4">
                <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg bg-stone-100">
                  {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm text-ink">{item.name}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(item.productId)}
                      aria-label="Remove item"
                      className="text-stone-500"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-ink">{formatMoney(item.priceCents, item.currency)}</span>
                    <div className="flex items-center gap-3 rounded-pill border border-stone-300 px-2.5 py-1.5">
                      <button
                        type="button"
                        onClick={() => updateQty(item.productId, item.qty - 1)}
                        aria-label="Decrease quantity"
                      >
                        <MinusIcon />
                      </button>
                      <span className="w-3 text-center text-xs font-semibold">{item.qty}</span>
                      <button
                        type="button"
                        onClick={() => updateQty(item.productId, item.qty + 1)}
                        aria-label="Increase quantity"
                        disabled={item.qty >= item.stockQty}
                      >
                        <PlusIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-app flex-col gap-3 border-t border-stone-300 bg-white px-5 py-4 sm:absolute">
          {requiresDeposit && (
            <p className="rounded-lg bg-stone-100 px-3 py-2 text-xs leading-relaxed text-stone-700">
              One or more items require a deposit to secure your order — you'll pay that portion now and settle the
              rest on delivery.
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm text-stone-700">Subtotal</span>
            <span className="text-base font-semibold text-ink">{formatMoney(totalCents, currency)}</span>
          </div>
          <Button fullWidth onClick={() => navigate("/checkout")}>
            Proceed to Order
          </Button>
        </div>
      )}
    </StorefrontLayout>
  );
}
